import { sanitizeHostname } from 'shared/utils/sanitize-hostname'

interface ResolveHostnamePartsResult {
  hostname: string
  protocol?: string
}

function getCandidate(
  hasExplicitProtocol: boolean,
  protocolRelative: boolean,
  trimmed: string,
): string {
  if (hasExplicitProtocol) {
    return trimmed
  } else if (protocolRelative) {
    return `http:${trimmed}`
  } else {
    return `http://${trimmed}`
  }
}

/**
 * Normalizes a dashboard hostname string by extracting a clean host and an optional protocol.
 *
 * It accepts values in multiple shapes (e.g. `https://domain.com`, `//domain.com`, `domain.com`,
 * or `domain.com/path`) and attempts to return a consistent pair:
 *   - `hostname`: a sanitized host portion, free of protocol prefixes and path segments.
 *   - `protocol`: the explicitly declared protocol (`http` or `https`) when it can be inferred
 *     from the user input; otherwise `undefined`.
 *
 * The function is resilient to malformed URLs. When parsing with the URL constructor fails
 * we fallback to string-based normalization.
 *
 * @param rawHostname candidate hostname string to normalize
 * @returns normalized hostname with optional protocol information
 */
export const resolveHostnameParts = (rawHostname: string): ResolveHostnamePartsResult => {
  const trimmed = rawHostname.trim()

  if (!trimmed) {
    return { hostname: '' }
  }

  try {
    const hasExplicitProtocol = /^https?:\/\//i.test(trimmed)
    const protocolRelative = /^\/\//.test(trimmed)
    const candidate: string = getCandidate(hasExplicitProtocol, protocolRelative, trimmed)

    const parsed = new URL(candidate)
    const hostname = parsed.host
    const protocol = hasExplicitProtocol ? parsed.protocol.replace(/:$/, '') : undefined

    return { hostname, protocol }
  } catch {
    const withoutProtocol = trimmed.replace(/^https?:\/\//i, '').replace(/^\/\//, '')
    const hostname = sanitizeHostname(withoutProtocol.split('/')[0], { toLowerCase: false })
    return { hostname }
  }
}
